import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { desktopSecureStorage, isTauri } from '@/lib/utils/tauri'

import { isValidUUID } from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import type { IntentStoreKind } from './intent-store-scopes'
import { createAtomicIntentStore } from './intent-store'
import { intentStoreScopes as scopes } from './intent-store-scopes'

export type { IntentStoreKind } from './intent-store-scopes'

export function readIntentOwner(
  kind: IntentStoreKind,
  key: string,
): string | null {
  const [, prefix, shape] = scopes[kind]
  const fields: readonly ('id' | 'nullable-id' | 'financial-kind')[] = shape
  if (!key.startsWith(prefix)) return null
  try {
    const parts: unknown = JSON.parse(key.slice(prefix.length))
    if (!Array.isArray(parts) || parts.length !== fields.length) return null
    if (
      !fields.every((field, index) => {
        const value: unknown = parts[index]
        if (field === 'nullable-id' && value === null) return true
        return (
          typeof value === 'string' &&
          (field === 'financial-kind'
            ? value === 'expense' || value === 'contractor-invoice'
            : isValidUUID(value))
        )
      })
    )
      return null
    return parts[0] as string
  } catch {
    return null
  }
}

/** The store calls this synchronously, before opening/reading IndexedDB. */
export function captureIntentOwner(ownerId: string) {
  if (!isTauri()) return undefined
  const expected = desktopAccountSession.capture()
  const assertCurrent = () => {
    expected.assertCurrent()
    if (expected.identity !== ownerId) throw new SessionChangedError()
  }
  return Object.assign(
    async () => {
      await desktopSecureStorage.assertSessionCurrent(expected)
      assertCurrent()
    },
    { assertCurrent },
  )
}

export function createOwnedIntentStore(
  kind: IntentStoreKind,
  getFactory: () => IDBFactory = () => window.indexedDB,
  storageError?: () => Error,
  databaseName: string = scopes[kind][0],
) {
  return createAtomicIntentStore(
    getFactory,
    databaseName,
    storageError,
    key => readIntentOwner(kind, key),
    captureIntentOwner,
  )
}
